import { Link } from "react-router-dom";
import { ArrowUpDown, Instagram, Radio, Send, Youtube } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { StarButton } from "@/components/Stars";
import { TIER_COLORS } from "@/components/LiveIntelTable";
import { CYAN, bl } from "@/lib/c360Mine";
import { day } from "@/lib/c360Profile";
import { AVENUES, AV_NAME, AV_SHORT, AV_UNKNOWN, BOT_LEVEL, BOT_NAME, LAST, MARKET, RATING, lastKey, niceDate, short } from "@/lib/creator360";
import { botLineText, openedBots, useOpenedBots } from "@/lib/botReasons";

// v77 — Creator 360 list pieces.
export const AV_ICON = { yt: Youtube, live: Radio, tg: Send, ig: Instagram };

export function Tip({ content, children, testid, wide }) {
  return (
    <Tooltip delayDuration={120}>
      <TooltipTrigger asChild><span data-testid={testid} className="cursor-help">{children}</span></TooltipTrigger>
      <TooltipContent className={`${wide ? "max-w-[360px]" : "max-w-[280px]"} p-3 text-xs leading-snug text-zinc-300 border hairline`} style={{ background: "var(--surface-2)" }} data-testid={testid ? `${testid}-tip` : undefined}>{content}</TooltipContent>
    </Tooltip>
  );
}

export function Bars({ rating, size = 12, color }) {
  const r = RATING[rating] || RATING.unknown;
  const c = color || r.color;
  return (
    <span className="inline-flex items-end gap-[2px]" style={{ height: size }} data-bars={r.bars}>
      {[0.45, 0.72, 1].map((h, i) => (
        <span key={i} className="w-[3px] rounded-[1px]" style={{ height: `${h * 100}%`, background: i < r.bars ? c : "rgba(113,113,122,0.45)" }} />
      ))}
    </span>
  );
}

function chipStyle(rating, mine) {
  if (mine) return { border: `1px solid ${CYAN}88`, color: CYAN, background: "rgba(0,240,255,0.08)" };
  if (rating === "unknown") return { border: "1px dashed rgba(113,113,122,0.6)", color: "#71717A", background: "transparent" };
  const c = RATING[rating].color;
  return { border: `1px solid ${c}66`, color: c, background: `${c}12` };
}

function MineTip({ m, kol }) {
  return (
    <div className="rounded border px-2.5 py-2 text-cyan-100" style={{ borderColor: `${CYAN}55`, background: "rgba(0,240,255,0.06)" }} data-testid="c360-tip-mine">
      <div>{m.r ? `Changed by you on ${day(m.at)} · KOL Radar said ${RATING[kol].label}` : `You changed ${Object.keys(m.checks || {}).length === 1 ? "a check" : "some checks"} on ${day(m.at)} · the verdict is KOL Radar's`}</div>
      {m.note && <div className="text-cyan-200/75 mt-1">"{m.note}"</div>}
    </div>
  );
}

function AvenueTip({ a, x, bot, open, m }) {
  const r = RATING[x.r];
  const mr = m?.r;
  return (
    <div className="space-y-1.5" data-rating={x.r}>
      <div className="flex items-center justify-between gap-4">
        <span className="font-semibold text-white">{AV_NAME[a]}</span>
        <span className="mono text-[11px]" style={{ color: mr ? CYAN : r.color }}>{mr ? `${RATING[mr].label} · your call` : r.label}</span>
      </div>
      {m && <MineTip m={m} kol={x.r} />}
      {mr && <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 pt-1">KOL Radar's decision · {r.label}</div>}
      {x.r === "unknown" ? <div>{AV_UNKNOWN[a]}</div> : x.lines.map((l, i) => <div key={i}>{l}</div>)}
      {bot && <div className="text-zinc-400" data-testid="c360-tip-bot">{BOT_NAME[a]}: {botLineText(a, bot)}</div>}
      {open && <div className="text-cyan-300/80 pt-1 border-t border-white/10" data-testid="c360-tip-open">Click to open {AV_NAME[a]}</div>}
    </div>
  );
}

export function AvenueChip({ r, a, onOpen }) {
  useOpenedBots();
  const x = r.av[a];
  const m = r.mine?.[a];
  const shown = m?.r || x.r;
  const Icon = AV_ICON[a];
  const bot = (r.bot || openedBots(r.id))?.[a];
  return (
    <Tip wide testid={`c360-av-${a}-${r.id}`} content={<AvenueTip a={a} x={x} bot={bot} open={!!onOpen} m={m} />}>
      <span data-rating={x.r} data-mine={m?.r || (m ? "checks" : undefined)} role={onOpen ? "button" : undefined} onClick={onOpen ? () => onOpen(r.id, a) : undefined} data-testid={`c360-chip-${a}-${r.id}`} className="relative cursor-pointer h-7 px-2 rounded-md inline-flex items-center gap-1.5 mono text-[11px] whitespace-nowrap transition-transform duration-150 hover:-translate-y-px" style={chipStyle(shown, !!m?.r)}>
        <Icon size={12} strokeWidth={1.8} />
        <span className={shown === "unknown" ? "" : "text-white/90"}>{shown === "unknown" || x.n == null ? "—" : short(x.n)}</span>
        {shown !== "unknown" && <Bars rating={shown} size={11} color={m?.r ? CYAN : undefined} />}
        {m && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full border border-black" style={{ background: CYAN }} data-testid={`c360-chip-dot-${a}-${r.id}`} />}
      </span>
    </Tip>
  );
}

const pill = (active) => active
  ? { background: "rgba(57,255,20,0.08)", color: "#39FF14", border: "1px solid rgba(57,255,20,0.5)" }
  : { background: "rgba(244,63,94,0.08)", color: "#F43F5E", border: "1px solid rgba(244,63,94,0.5)" };

export function exHover(e) {
  if (e.active) return `Promoting ${e.name} (on YouTube) · first promo ${niceDate(e.since) || "date not known"} (${e.days} days)`;
  return `Stopped promoting ${e.name} (on YouTube) · last promo ${niceDate(e.last) || "date not known"} (${e.days_off} days off)`;
}

function ExBadge({ e, id }) {
  return (
    <Tip content={exHover(e)} testid={`c360-ex-${id}-${e.key}`}>
      <span data-status={e.active ? "active" : "stopped"} className="mono text-[10px] h-[22px] px-1.5 rounded inline-flex items-center gap-1.5 whitespace-nowrap" style={pill(e.active)}>
        {e.name}<span className="text-[9px] opacity-75">{e.active ? `${e.days}d` : `${e.days_off}d off`}</span>
      </span>
    </Tip>
  );
}

export function Exchanges({ r }) {
  if (!r.ex.length) {
    return (
      <div data-testid={`c360-unaff-${r.id}`}>
        <span className="mono text-[10px] h-[22px] px-1.5 rounded inline-flex items-center text-zinc-300" style={{ border: "1px dashed rgba(161,161,170,0.6)" }}>Unaffiliated</span>
        <div className="mono text-[10px] text-zinc-500 mt-1">Promotes no exchange</div>
      </div>
    );
  }
  const now = r.ex.filter((e) => e.active).length;
  const stopped = r.ex.length - now;
  const shown = r.ex.slice(0, 5);
  const rest = r.ex.slice(5);
  return (
    <div className="max-w-[300px]" data-testid={`c360-exchanges-${r.id}`}>
      <div className="flex flex-wrap gap-1">
        {shown.map((e) => <ExBadge key={`${e.cat}-${e.key}`} e={e} id={r.id} />)}
        {rest.length > 0 && (
          <Tip testid={`c360-ex-more-${r.id}`} content={<div className="space-y-0.5">{rest.map((e) => <div key={e.key}>{exHover(e)}</div>)}</div>} wide>
            <span className="mono text-[10px] h-[22px] px-1.5 rounded inline-flex items-center border hairline text-zinc-400">+{rest.length}</span>
          </Tip>
        )}
      </div>
      <div className="mono text-[10px] text-zinc-500 mt-1" data-testid={`c360-ex-sum-${r.id}`}>
        {now ? [`${now} now`, stopped ? `${stopped} stopped` : null].filter(Boolean).join(" · ") : `Stopped all · ${stopped} before`}
      </div>
    </div>
  );
}

export function Market({ r }) {
  const c = ["crypto", "both", "un_crypto"].includes(r.mkt);
  const t = ["tradfi", "both", "un_tradfi"].includes(r.mkt);
  return (
    <span className="inline-flex items-center gap-1.5 mono text-[11px] text-zinc-300 whitespace-nowrap" data-testid={`c360-market-${r.id}`}>
      <span className="inline-flex gap-[2px]">
        <span className="w-2 h-2 rounded-[2px]" style={{ background: c ? "#00F0FF" : "#3F3F46" }} />
        <span className="w-2 h-2 rounded-[2px]" style={{ background: t ? "#FBBF24" : "#3F3F46" }} />
      </span>
      {MARKET[r.mkt]}
    </span>
  );
}

export function botSummary(bots) {
  const flags = AVENUES.filter((a) => bots[a] === "high" || bots[a] === "medium");
  const unchecked = AVENUES.filter((a) => bots[a] === "not_checked").length;
  const worst = flags.sort((x, y) => (bots[x] === "high" ? 0 : 1) - (bots[y] === "high" ? 0 : 1));
  return { worst, unchecked };
}

export function BotCell({ r }) {
  const { worst, unchecked } = botSummary(r.bots);
  return (
    <div data-testid={`c360-bot-${r.id}`}>
      <div className="flex gap-1">
        {AVENUES.map((a) => {
          const b = BOT_LEVEL[r.bots[a]] || BOT_LEVEL.not_checked;
          const Icon = AV_ICON[a];
          return (
            <Tip key={a} testid={`c360-bot-${a}-${r.id}`} content={`${BOT_NAME[a]}: ${b.label}. ${b.hint}`}>
              <span data-level={r.bots[a]} className="w-6 h-6 rounded inline-flex items-center justify-center" style={{ border: `1px solid ${b.color}66`, color: b.color, background: `${b.color}10` }}>
                <Icon size={11} strokeWidth={1.8} />
              </span>
            </Tip>
          );
        })}
      </div>
      <div className="text-[11px] mt-1 whitespace-nowrap" data-testid={`c360-bot-sum-${r.id}`}>
        {worst.length
          ? worst.map((a) => <span key={a} className="mr-2" style={{ color: BOT_LEVEL[r.bots[a]].color }}>{AV_SHORT[a]}: {BOT_LEVEL[r.bots[a]].label}</span>)
          : <span style={{ color: "#39FF14" }}>No flags</span>}
      </div>
      {unchecked > 0 && <div className="mono text-[10px] text-zinc-500">{unchecked} not checked yet</div>}
    </div>
  );
}

export function LastActive({ r }) {
  const k = lastKey(r.days);
  const l = LAST[k];
  const hover = l.hover ? `Their last YouTube upload (standard video, Short or live) was ${l.hover}.` : "Their last YouTube upload is not known yet.";
  return (
    <Tip content={hover} testid={`c360-last-${r.id}`}>
      <span className="block whitespace-nowrap" data-key={k}>
        <span className="inline-flex items-center gap-1.5 text-[13px]" style={{ color: l.color }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: l.color }} />{l.label}
        </span>
        <span className="block mono text-[10px] text-zinc-500 pl-3">last upload</span>
      </span>
    </Tip>
  );
}

export const profileHref = (r) => (r.seg === "unaffiliated" ? `/unaffiliated/${r.id}` : `/kol/${r.id}`);

function CreatorCell({ r, star, onOpen, blur }) {
  return (
    <td className="px-4 py-3 min-w-[220px] max-w-[280px]">
      <div className="flex items-center gap-2 min-w-0">
        {star && <StarButton on={star.has(r.id)} onToggle={() => star.toggle(r.id)} testid={`c360-star-${r.id}`} size={14} />}
        {onOpen
          ? <button type="button" onClick={() => onOpen(r.id, "best")} data-testid={`c360-name-${r.id}`} className="text-left text-white font-medium hover:text-cyan-400 truncate transition-colors"><span className={bl(blur)}>{r.name}</span></button>
          : <Link to={profileHref(r)} data-testid={`c360-name-${r.id}`} className="text-white font-medium hover:text-cyan-400 truncate transition-colors"><span className={bl(blur)}>{r.name}</span></Link>}
        {r.pinned && <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 shrink-0" data-testid={`c360-pinned-${r.id}`}>Pinned</span>}
      </div>
      <div className="mono text-[10px] text-zinc-500 flex items-center gap-2 mt-1 pl-6 flex-wrap">
        <span className="font-semibold" style={{ color: TIER_COLORS[r.tier] || "#94A3B8" }} data-testid={`c360-tier-${r.id}`}>Tier {r.tier}</span>
        <span>{short(r.subs)} subs</span>
        {r.yt && <a href={r.yt} target="_blank" rel="noreferrer" className={`hover:text-cyan-400 ${bl(blur, "link")}`} data-testid={`c360-link-yt-${r.id}`}>YT</a>}
        {r.tg && <a href={r.tg} target="_blank" rel="noreferrer" className={`hover:text-cyan-400 ${bl(blur, "link")}`} data-testid={`c360-link-tg-${r.id}`}>TG</a>}
        {r.ig && <a href={r.ig} target="_blank" rel="noreferrer" className={`hover:text-cyan-400 ${bl(blur, "link")}`} data-testid={`c360-link-ig-${r.id}`}>IG</a>}
      </div>
      {r.fav_outside && <div className="mono text-[10px] text-amber-300/70 mt-0.5 pl-6" data-testid={`c360-fav-outside-${r.id}`}>Favourite · outside your filters</div>}
    </td>
  );
}

export function GemRow({ r, star, botCol, onOpen, blur }) {
  return (
    <tr className="border-t hairline hover:bg-white/[0.02] transition-colors" data-testid={`c360-row-${r.id}`} data-blurred={blur ? "1" : "0"} data-blur-scope={r.id}>
      <CreatorCell r={r} star={star} onOpen={onOpen} blur={blur} />
      <td className="px-4 py-3"><Exchanges r={r} /></td>
      <td className="px-4 py-3"><Market r={r} /></td>
      <td className="px-4 py-3"><div className="flex gap-1.5" data-testid={`c360-avenues-${r.id}`}>{AVENUES.map((a) => <AvenueChip key={a} r={r} a={a} onOpen={onOpen} />)}</div></td>
      {botCol && <td className="px-4 py-3">{r.bots && <BotCell r={r} />}</td>}
      <td className="px-4 py-3"><LastActive r={r} /></td>
    </tr>
  );
}

export function Head({ label, sub, k, sort, onSort }) {
  const on = k && (sort.sort ?? sort.key) === k;
  return (
    <th className="px-4 py-3 text-left font-normal align-top whitespace-nowrap">
      {k ? (
        <button type="button" onClick={() => onSort(k)} data-testid={`c360-sort-head-${k}`}
          className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.15em] transition-colors ${on ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
          {label}<ArrowUpDown size={11} className={on ? "text-cyan-400" : "text-zinc-600"} />
          {on && <span className="text-[9px]" data-testid={`c360-sort-dir-${k}`}>{sort.order === "desc" ? "↓" : "↑"}</span>}
        </button>
      ) : <span className="mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</span>}
      {sub && <div className="mono text-[9px] normal-case tracking-normal text-zinc-600 mt-0.5">{sub}</div>}
    </th>
  );
}

export function Legend() {
  return (
    <div className="px-4 py-3 border-t hairline flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-400" data-testid="c360-legend">
      <span className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Verdict</span>
      {["strong", "good", "weak", "unknown"].map((k) => (
        <span key={k} className="inline-flex items-center gap-1.5"><Bars rating={k} />{k === "unknown" ? "Not known (never counted as Weak)" : RATING[k].label}</span>
      ))}
      <span className="inline-flex items-center gap-1.5" data-testid="c360-legend-mine"><span style={{ color: CYAN }}>●</span> Cyan = a verdict you changed (hover for KOL Radar's own)</span>
    </div>
  );
}

export function Pager({ total, page, size, setPage, setSize }) {
  const pages = Math.max(1, Math.ceil(total / size));
  const nums = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(pages, page + 2); p++) nums.push(p);
  const b = "px-2.5 py-1 rounded border hairline text-xs transition-colors disabled:opacity-30 disabled:pointer-events-none hover:border-white/30";
  return (
    <div className="px-4 py-3 border-t hairline flex items-center justify-between gap-3 flex-wrap text-xs text-zinc-500" data-testid="c360-pager">
      <span className="flex items-center gap-2">Rows per page
        {[10, 25, 50].map((n) => (
          <button key={n} type="button" onClick={() => setSize(n)} data-testid={`c360-size-${n}`} className={`${b} ${n === size ? "text-white border-white/40 bg-white/5" : "text-zinc-400"}`}>{n}</button>
        ))}
      </span>
      <span className="flex items-center gap-1.5">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="c360-prev" className={`${b} text-zinc-300`}>Prev</button>
        {nums.map((p) => (
          <button key={p} type="button" onClick={() => setPage(p)} data-testid={`c360-page-${p}`} className={`${b} ${p === page ? "text-white border-white/40 bg-white/5" : "text-zinc-400"}`}>{p}</button>
        ))}
        <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="c360-next" className={`${b} text-zinc-300`}>Next</button>
      </span>
    </div>
  );
}
